/**
 * The release-note "markdown" — `**bold**` and `[text](url)` only — in one module, used by the editor's
 * serializer and by every renderer so the two cannot disagree (RA-2695).
 *
 * 🔴 Authored text is never turned into HTML by string: renderers build nodes from {@link parseInline}
 * segments, and the serializer reads a DOM back into the string format. Links are honoured only for
 * `http(s)://` URLs — one rule everywhere — and anything else stays literal text.
 */

/** One run of rendered text. Adjacent runs never share both `bold` and `href`. */
export interface Segment {
  text: string;
  bold: boolean;
  href?: string;
}

const SAFE_HREF = /^https?:\/\//i;
const BOLD = /\*\*(.+?)\*\*/g;
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI']);

/** Whether a URL may become a link: `http://` or `https://`, case-insensitive. */
export function isSafeHref(href: string | null | undefined): boolean {
  return !!href && SAFE_HREF.test(href);
}

/** Splits authored text into bold / link / plain segments. Malformed markup stays literal. */
export function parseInline(text: string | null | undefined): Array<Segment> {
  const segments: Array<Segment> = [];
  if (!text) {
    return segments;
  }
  let last = 0;
  for (const link of findLinks(text)) {
    pushBold(segments, text.slice(last, link.start), undefined);
    if (isSafeHref(link.href)) {
      pushBold(segments, link.label, link.href);
    } else {
      pushBold(segments, text.slice(link.start, link.end), undefined);
    }
    last = link.end;
  }
  pushBold(segments, text.slice(last), undefined);
  return merge(segments);
}

/** Length as rendered: bold markers stripped, `[label](url)` counted as `label`. */
export function renderedLength(text: string | null | undefined): number {
  return parseInline(text).reduce((total, segment) => total + segment.text.length, 0);
}

/**
 * Replaces an element's children with the rendered form of `text`, built with `createElement` and
 * `textContent` only. Consecutive segments sharing a link go inside one `<a>`, so the round trip through
 * {@link serializeInline} reproduces the original string.
 */
export function renderInlineInto(el: HTMLElement, text: string | null | undefined): void {
  while (el.firstChild) {
    el.firstChild.remove();
  }
  let anchor: HTMLAnchorElement | null = null;
  for (const segment of parseInline(text)) {
    const run = runNode(segment);
    if (!segment.href) {
      anchor = null;
      el.appendChild(run);
      continue;
    }
    if (anchor?.getAttribute('href') !== segment.href) {
      anchor = document.createElement('a');
      anchor.setAttribute('href', segment.href);
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener');
      el.appendChild(anchor);
    }
    anchor.appendChild(run);
  }
}

/** Reads a contenteditable element back into one line of the string format. */
export function serializeInline(el: Node): string {
  return normalise(Array.from(el.childNodes).map(node => walk(node, false)).join(''));
}

/** Reads a contenteditable element into one entry per non-blank line (block child or `<br>`-separated run). */
export function serializeLines(el: Node): Array<string> {
  const lines: Array<string> = [];
  let run: Array<Node> = [];
  const flush = () => {
    lines.push(normalise(run.map(node => walk(node, false)).join('')));
    run = [];
  };
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName)) {
      flush();
      lines.push(...serializeLines(node));
    } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR') {
      flush();
    } else {
      run.push(node);
    }
  }
  flush();
  return lines.filter(line => line.length > 0);
}

interface LinkMatch {
  start: number;
  end: number;
  label: string;
  href: string;
}

/**
 * Every `[label](url)` left to right — exactly what `/\[([^\]]*)\]\(([^)\s]*)\)/g` finds — in linear time.
 * A failed candidate skips every `[` before its `]`, which would close there and fail too, and a URL run
 * that ended without `)` is never rescanned.
 */
function findLinks(text: string): Array<LinkMatch> {
  const links: Array<LinkMatch> = [];
  let failedUntil = -1;
  let open = text.indexOf('[');
  while (open !== -1) {
    const close = text.indexOf(']', open + 1);
    if (close === -1) {
      break;
    }
    let end = -1;
    if (text[close + 1] === '(' && close + 2 > failedUntil) {
      end = urlEnd(text, close + 2);
      if (end < 0) {
        failedUntil = -end - 1;
      }
    }
    if (end >= 0) {
      links.push({ start: open, end: end + 1, label: text.slice(open + 1, close), href: text.slice(close + 2, end) });
      open = text.indexOf('[', end + 1);
    } else {
      open = text.indexOf('[', close + 1);
    }
  }
  return links;
}

/** The index of the `)` closing a URL run from `from`, or `-(stop + 1)` when whitespace or the end comes first. */
function urlEnd(text: string, from: number): number {
  let i = from;
  while (i < text.length && text[i] !== ')' && !/\s/.test(text[i])) {
    i++;
  }
  return text[i] === ')' ? i : -(i + 1);
}

function pushBold(segments: Array<Segment>, text: string, href: string | undefined): void {
  if (!text) {
    return;
  }
  let last = 0;
  for (const match of text.matchAll(BOLD)) {
    pushRun(segments, text.slice(last, match.index), false, href);
    pushRun(segments, match[1], true, href);
    last = match.index + match[0].length;
  }
  pushRun(segments, text.slice(last), false, href);
}

function pushRun(segments: Array<Segment>, text: string, bold: boolean, href: string | undefined): void {
  if (text) {
    segments.push(href ? { text, bold, href } : { text, bold });
  }
}

function merge(segments: Array<Segment>): Array<Segment> {
  return segments.reduce((merged: Array<Segment>, segment) => {
    const previous = merged.at(-1);
    if (previous?.bold === segment.bold && previous.href === segment.href) {
      previous.text += segment.text;
    } else {
      merged.push({ ...segment });
    }
    return merged;
  }, []);
}

function runNode(segment: Segment): Node {
  const text = document.createTextNode(segment.text);
  if (!segment.bold) {
    return text;
  }
  const bold = document.createElement('b');
  bold.appendChild(text);
  return bold;
}

function walk(node: Node, inBold: boolean): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').replaceAll('\u00a0', ' ');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node as Element;
  const tag = el.tagName;
  if (tag === 'BR') {
    return ' ';
  }
  const isBold = tag === 'B' || tag === 'STRONG';
  if (isBold && !inBold && el.querySelector('a')) {
    return boldAroundLinks(el);
  }
  const inner = Array.from(el.childNodes).map(child => walk(child, inBold || isBold)).join('');
  if (isBold && !inBold) {
    return wrap(inner, core => `**${core}**`);
  }
  if (tag === 'A') {
    const href = el.getAttribute('href');
    return isSafeHref(href) ? wrap(inner, core => `[${core}](${href})`) : inner;
  }
  return inner;
}

/**
 * Serialises a bold run that contains a link. `**a [b](u) c**` would not parse — links are found before
 * bold, which splits the markers — so bold is closed around each link and moved inside its label instead:
 * `**a** [**b**](u) **c**`, which renders identically and round-trips.
 */
function boldAroundLinks(el: Element): string {
  return Array.from(el.childNodes).map(child => {
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'A') {
      const href = (child as Element).getAttribute('href');
      const label = wrap(Array.from(child.childNodes).map(node => walk(node, true)).join(''), core => `**${core}**`);
      return isSafeHref(href) ? wrap(label, core => `[${core}](${href})`) : label;
    }
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).querySelector('a')) {
      return boldAroundLinks(child as Element);
    }
    return wrap(walk(child, true), core => `**${core}**`);
  }).join('');
}

/** Wraps the non-whitespace core of `inner`, keeping edge whitespace outside the markers. */
function wrap(inner: string, decorate: (core: string) => string): string {
  const core = inner.trim();
  if (!core) {
    return inner;
  }
  const start = inner.length - inner.trimStart().length;
  return inner.slice(0, start) + decorate(core) + inner.slice(start + core.length);
}

function normalise(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}
